import PillarTableFilterCheckbox from "shared/components/pillar-table/filters/PillarTableFilterCheckbox";
import PillarTableFilterDatePicker from "shared/components/pillar-table/filters/PillarTableFilterDatePicker";
import {
  type PillarTableFilterOptionSource,
  usePillarTableFilterOptionSources,
} from "shared/components/pillar-table/filters/PillarTableFilterOptionSources";
import PillarTableFilterSelectMenu from "shared/components/pillar-table/filters/PillarTableFilterSelectMenu";
import PillarTableFilterTextInput from "shared/components/pillar-table/filters/PillarTableFilterTextInput";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import { useSocietySession } from "shared/context/SocietySessionContext";
import {
  type AppFilterOption,
  FilterComponentType,
  type FilterOptionComponent,
} from "shared/filter-where-clause";
import { base_table } from "shared/prisma/base-table";

const PillarTableFiltersCustomReport = () => {
  const { customReportResponse, setFilters } = usePillarTableContext();
  const filters = customReportResponse?.filtersConfig ?? [];

  // Raw-query reports have no filters (non-admins don't get their SQL)
  if (
    filters.length === 0 ||
    customReportResponse?.baseTable === base_table.raw_query
  ) {
    return null;
  }

  return (
    <div className="w-full flex justify-between py-1">
      <div className="mb-1 flex justify-start items-end w-full gap-x-1 gap-y-1 flex-wrap">
        {filters.map((item) => {
          const type = item.component?.type as FilterComponentType;
          const Component = FilterComponentMap[type];
          if (!Component) {
            return null;
          }

          // spread EVERY field of item into the Component
          return <Component key={item.filterKey} {...item} />;
        })}
        <button
          data-testid="clear-filters-button"
          type="button"
          className="p-1 h-4.75 border rounded border-neutral-mid-200 text-filter-label mr-0.25 whitespace-nowrap"
          onClick={() => setFilters({})}
        >
          Clear Filters
        </button>
      </div>
    </div>
  );
};

export default PillarTableFiltersCustomReport;

type TableOptionSourceProps = {
  /** The report filter's component, with any stored choices */
  component: FilterOptionComponent;
  /** The report filter's key, which the table's menu must share */
  filterKey: string;
  /** The table's option sources, when a default report replaced its filters */
  optionSources: ReturnType<typeof usePillarTableFilterOptionSources>;
};

/**
 * How a report menu that stores no choices (no list, or an empty one) loads
 * its options: the way the table's own menu of the same filterKey does. Only
 * when a default report replaced the table's filters and that menu has options
 * of its own; otherwise undefined, and the menu keeps what the report stores.
 */
const tableOptionSource = ({
  component,
  filterKey,
  optionSources,
}: TableOptionSourceProps): PillarTableFilterOptionSource | undefined =>
  component.choices?.length ? undefined : optionSources?.sources.get(filterKey);

type FilterComponentMap = {
  [K in FilterComponentType]?: React.FC<AppFilterOption>;
};

const FilterComponentMap: FilterComponentMap = {
  [FilterComponentType.none]: () => null,

  [FilterComponentType.search]: (opt) => {
    const { filterKey, component } = opt;
    // if missing, nothing to render
    if (!filterKey || !component?.label) {
      return null;
    }

    return (
      <PillarTableFilterTextInput
        filterKey={filterKey}
        testId={filterKey}
        title={component.label}
        toolTip={component.toolTip}
        className="w-full min-w-4xl"
      />
    );
  },

  [FilterComponentType.Date]: (opt) => {
    const { filterKey, component } = opt;
    const society = useSocietySession();
    if (!filterKey || !component?.label) {
      return null;
    }

    return (
      <div className="flex flex-col min-w-6xl">
        <PillarTableFilterDatePicker
          filterKey={filterKey}
          title={component.label}
          toolTip={component.toolTip}
          society={society}
        />
      </div>
    );
  },

  [FilterComponentType.selectMenuCheckbox]: (opt) => {
    const { filterKey, component = [] } = opt;
    const optionSources = usePillarTableFilterOptionSources();
    if (
      !filterKey ||
      !component ||
      !("label" in component) ||
      !component.label
    ) {
      return null;
    }
    const tableSource = tableOptionSource({
      component,
      filterKey,
      optionSources,
    });

    return (
      <PillarTableFilterSelectMenu
        isMultiple
        filterKey={filterKey}
        title={component.label}
        toolTip={"toolTip" in component ? component.toolTip : undefined}
        {...(tableSource ?? { choices: component.choices })}
        defaultValue={
          "defaultValue" in component
            ? (component.defaultValue as string[])
            : undefined
        }
        className="min-w-[160px] block pillartable-filters-selectmenu-dropdown-container"
      />
    );
  },

  [FilterComponentType.selectMenu]: (opt) => {
    const { filterKey, component } = opt;
    const optionSources = usePillarTableFilterOptionSources();
    if (!filterKey || !component || !component.label) {
      return null;
    }
    const tableSource = tableOptionSource({
      component,
      filterKey,
      optionSources,
    });
    // Not drawn without a stored list, unless the table's menu lends its options
    if (!Array.isArray(component.choices) && !tableSource) {
      return null;
    }

    return (
      <PillarTableFilterSelectMenu
        isMultiple={false}
        filterKey={filterKey}
        title={component.label}
        toolTip={component.toolTip}
        {...(tableSource ?? {
          choices: (component.choices ?? []).map((choice) => ({
            value: choice.value,
            display: choice.display,
          })),
        })}
        defaultValue={component.defaultValue as string | undefined}
        className="min-w-[160px] block pillartable-filters-selectmenu-dropdown-container"
      />
    );
  },

  [FilterComponentType.checkbox]: (opt) => {
    const { filterKey, component } = opt;
    if (!filterKey || !component?.label) {
      return null;
    }

    return (
      <PillarTableFilterCheckbox
        filterKey={filterKey}
        title={component.label}
        toolTip={component.toolTip}
        isRowStyle
      />
    );
  },
};
